/**
 * Registry and state boundary for interactive renderers registered with chat's
 * renderMessage dispatch.
 *
 * To add a new UI tool:
 * 1. Register its part type with the corresponding component
 * 2. Create the renderer component in src/components/chat/
 *
 * Also holds the shared bubble max-width rule used by ChatWidget, ChatMessage, and renderers.
 */
import type React from 'react';

import type { ChatMessageProps, ChatMode } from './types';

type UIToolRenderer = React.FC<ChatMessageProps>;
export type UiToolRenderState = 'loading' | 'available' | 'error' | 'hidden';

const PANEL_BUBBLE_MAX_WIDTH = '85%';
const FULLSCREEN_BUBBLE_MAX_WIDTH = '80%';

let renderers: Record<string, UIToolRenderer> = {};

export function getBubbleMaxWidth (mode?: ChatMode): string {
  return mode === 'fullscreen' ? FULLSCREEN_BUBBLE_MAX_WIDTH : PANEL_BUBBLE_MAX_WIDTH;
}

export function getUiToolRenderState (state: unknown, isLastMessage: boolean, isLoading: boolean): UiToolRenderState {
  if (state === 'input-streaming') return isLastMessage && isLoading ? 'loading' : 'hidden';
  if (state === 'input-available' || state === 'output-available') return 'available';
  if (state === 'output-error') return 'error';
  return 'hidden';
}

export function registerUiToolRenderers (map: Record<string, UIToolRenderer>): void {
  renderers = { ...renderers, ...map };
}

export function isUiTool (partType: string): boolean {
  return partType in renderers;
}

export function getUiToolRenderer (partType: string): UIToolRenderer | undefined {
  return renderers[partType];
}
